import { useActiveOrganization } from "@/context/ActiveOrganizationProvider";
import { EditorMode, EditorPromptState } from "@/context/PromptEditorContext";
import { VariablesWithValues } from "@/services/logs.service";
import { ChatMessage, ChatMessageData, Model } from "@/services/promptEditor.service";
import { isOrgToolConfig, OrgToolConfig, useOrgToolConfigs } from "@/services/v4.service";
import { Agent, AgentCallRequest, AgentKernelRequest, AgentToolRequest, isAgentToolRequest } from "@/types/app/agent";
import { File, FileWithSchema } from "@/types/app/file";
import {
  JSON_SCHEMA_RESPONSE_FORMAT,
  Prompt,
  PromptCallRequest,
  PromptKernelRequest,
  PromptToolRequest,
  ToolUsage,
} from "@/types/app/prompt";
import _, { pick } from "lodash";
import { useMemo } from "react";

const initialModel = {
  id: "gpt-4o-mini",
  name: "gpt-4o-mini",
  provider: "openai",
  hidden: false,
  fine_tuned: false,
  key: "gpt-4o-mini",
  max_prompt_tokens: 128000,
  max_output_tokens: 4096,
  tool_support: true,
  image_support: true,
  cost_per_prompt_token: 0.00000015,
  cost_per_output_token: 0.0000006,
  reasoning_effort_support: false,
  json_schema_response_support: true,
} as const satisfies Model;

// Used for the initial state of the prompt in the editor, and fallbacks for parameters.
export const INITIAL_EDITOR_PROMPT_STATE = {
  model: initialModel.name,
  provider: initialModel.provider,
  endpoint: "chat",
  prompt_template: "",
  chat_template: [],
  temperature: 1,
  max_tokens: -1,
  top_p: 1,
  frequency_penalty: 0,
  presence_penalty: 0,
  stop: undefined,
  other: {},
  tools: [],
  max_iterations: 5,
} satisfies EditorPromptState;

// This is true for OpenAI. Probably not for other providers.
// This is the API default. This is not the editor default (see: INITIAL_EDITOR_PROMPT_STATE)
export const DEFAULT_EDITOR_PROMPT_STATE = {
  model: initialModel.name,
  provider: initialModel.provider,
  endpoint: "chat",
  prompt_template: undefined,
  chat_template: undefined,
  temperature: 1,
  max_tokens: -1,
  top_p: 1,
  frequency_penalty: 0,
  presence_penalty: 0,
  // TODO: remove these. shouldn't be in the prompt.
  // @ts-expect-error
  frequency_penalty_range: {
    min: -2,
    max: 2,
  },
  presence_penalty_range: {
    min: -2,
    max: 2,
  },
  //end
  seed: undefined,
  response_format: undefined,
  stop: undefined,
  reasoning_effort: "medium",
  template_language: "default",
  other: {},
  tools: [],
  max_iterations: 5,
} satisfies EditorPromptState;

export const standardizeEditorPromptState = (
  editorPromptState: EditorPromptState,
  modelDetails?: Model,
): EditorPromptState => {
  const supportsReasoning = modelDetails?.reasoning_effort_support;
  // If model not found in the list, it's likely a custom model (e.g. from Azure).
  // Default to true to allow JSON schema functionality.
  // Worst case Azure will throw an error and we can handle it gracefully.
  const supportsJsonSchemaResponse = modelDetails?.json_schema_response_support ?? true;

  const isAnthropicThinkingModel = modelDetails?.provider === "anthropic" && supportsReasoning;
  const usesAnthropicReasoning =
    isAnthropicThinkingModel &&
    !_.isNil(editorPromptState.reasoning_effort) &&
    typeof editorPromptState.reasoning_effort === "number";

  const isOpenAIThinkingModel =
    (modelDetails?.provider === "openai" || modelDetails?.provider === "openai_azure") && supportsReasoning;

  // TODO: Improve. For example nullish coalescing with `??` might be better.
  const standardised = {
    ...editorPromptState,
    model: editorPromptState.model || DEFAULT_EDITOR_PROMPT_STATE.model,
    provider: editorPromptState.provider || DEFAULT_EDITOR_PROMPT_STATE.provider,
    endpoint: editorPromptState.endpoint || DEFAULT_EDITOR_PROMPT_STATE.endpoint,
    prompt_template: editorPromptState.prompt_template || DEFAULT_EDITOR_PROMPT_STATE.prompt_template,
    chat_template: editorPromptState.chat_template || DEFAULT_EDITOR_PROMPT_STATE.chat_template,
    temperature: usesAnthropicReasoning
      ? undefined
      : !_.isNil(editorPromptState.temperature)
        ? editorPromptState.temperature
        : DEFAULT_EDITOR_PROMPT_STATE.temperature,
    max_tokens: !_.isNil(editorPromptState.max_tokens)
      ? editorPromptState.max_tokens
      : DEFAULT_EDITOR_PROMPT_STATE.max_tokens,
    top_p: usesAnthropicReasoning
      ? undefined
      : !_.isNil(editorPromptState.top_p)
        ? editorPromptState.top_p
        : DEFAULT_EDITOR_PROMPT_STATE.top_p,
    frequency_penalty: !_.isNil(editorPromptState.frequency_penalty)
      ? editorPromptState.frequency_penalty
      : DEFAULT_EDITOR_PROMPT_STATE.frequency_penalty,
    presence_penalty: !_.isNil(editorPromptState.presence_penalty)
      ? editorPromptState.presence_penalty
      : DEFAULT_EDITOR_PROMPT_STATE.presence_penalty,
    stop: !_.isEmpty(editorPromptState.stop)
      ? typeof editorPromptState.stop === "string"
        ? [editorPromptState.stop]
        : editorPromptState.stop
      : DEFAULT_EDITOR_PROMPT_STATE.stop,
    seed: editorPromptState.seed || DEFAULT_EDITOR_PROMPT_STATE.seed,
    other: editorPromptState.other || DEFAULT_EDITOR_PROMPT_STATE.other,
    tools: editorPromptState.tools || DEFAULT_EDITOR_PROMPT_STATE.tools,
    max_iterations: editorPromptState.max_iterations || DEFAULT_EDITOR_PROMPT_STATE.max_iterations,
    // Explicitly remove response_format if the model does not support it, but
    // but allow JSON object for all models right now even though currently only OpenAI supports it.
    // TODO: supportsJsonSchemaResponse should be cleverer to know if model supports JSON object response too.
    response_format: supportsJsonSchemaResponse
      ? editorPromptState.response_format || DEFAULT_EDITOR_PROMPT_STATE.response_format
      : editorPromptState.response_format === JSON_SCHEMA_RESPONSE_FORMAT
        ? undefined
        : editorPromptState.response_format,
    // Explicitly reset reasoning_effort if the model does not support it.
    reasoning_effort: supportsReasoning
      ? isOpenAIThinkingModel
        ? editorPromptState.reasoning_effort && typeof editorPromptState.reasoning_effort === "string"
          ? editorPromptState.reasoning_effort
          : DEFAULT_EDITOR_PROMPT_STATE.reasoning_effort
        : // Anthropic models can have their reasoning_effort set to undefined, meaning the
          // request should be done without it.
          // If we switch from  OpenAI to Anthropic model, we turn off reasoning_effort
          // to avoid the default 'medium' value from OpenAI being used.
          isAnthropicThinkingModel && typeof editorPromptState.reasoning_effort === "string"
          ? undefined
          : editorPromptState.reasoning_effort
      : undefined,
    template_language: editorPromptState.template_language || DEFAULT_EDITOR_PROMPT_STATE.template_language,
  };

  return standardised;
};

/**
 * Reverse of `editorPromptStateToPrompt` and `editorPromptStateToAgent`
 */
export const getEditorPromptStateFromPrompt = (
  version: Prompt | Agent | PromptKernelRequest | AgentKernelRequest,
): EditorPromptState => {
  const tools: AgentToolRequest[] | PromptToolRequest[] = version.tools ?? [];
  // This is an explicit list WHICH IS A HUGE HAZARD because the Prompt will have loads of extra fields.
  return {
    model: version.model,
    provider: version.provider,
    endpoint: version.endpoint,
    template_language: version.template_language ?? undefined,
    reasoning_effort: version.reasoning_effort ?? undefined,
    max_tokens: version.max_tokens ?? undefined,
    temperature: version.temperature ?? undefined,
    top_p: version.top_p ?? undefined,
    stop: version.stop ?? undefined,
    frequency_penalty: version.frequency_penalty ?? undefined,
    presence_penalty: version.presence_penalty ?? undefined,
    seed: version.seed ?? undefined,
    response_format: version.response_format ?? undefined,
    other: version.other ?? null,
    prompt_template: typeof version.template === "string" ? version.template : undefined,
    chat_template: Array.isArray(version.template) ? version.template : undefined,
    tools,
    max_iterations: "max_iterations" in version ? version.max_iterations : undefined, // TODO: Add max_iterations to the agent response.
  };
};

/**
 * Map our non-standard editorPromptState to the PromptKernel.
 *
 * Reverse of `getEditorPromptStateFromPrompt`.
 * Explicit list of attributes is a hazard, but required to avoid passing in extra attributes
 * from editorPromptState like agent_tools.
 */
export const editorPromptStateToPrompt = ({
  editorPromptState,
  mode,
}: {
  editorPromptState: EditorPromptState;
  mode: EditorMode;
}): PromptKernelRequest => {
  return {
    // This nullish coalescing is practically redundant because of how many times this has been standardized, but the types are still optional...
    model: editorPromptState.model ?? DEFAULT_EDITOR_PROMPT_STATE.model,
    endpoint: editorPromptState.endpoint ?? DEFAULT_EDITOR_PROMPT_STATE.endpoint,
    template: (mode === "chat" ? editorPromptState.chat_template : editorPromptState.prompt_template) ?? null,
    provider: editorPromptState.provider ?? DEFAULT_EDITOR_PROMPT_STATE.provider,
    max_tokens: editorPromptState.max_tokens,
    temperature: editorPromptState.temperature,
    top_p: editorPromptState.top_p,
    stop: editorPromptState.stop,
    frequency_penalty: editorPromptState.frequency_penalty,
    presence_penalty: editorPromptState.presence_penalty,
    other: editorPromptState.other,
    seed: editorPromptState.seed,
    reasoning_effort: editorPromptState.reasoning_effort,
    response_format: editorPromptState.response_format,
    template_language: editorPromptState.template_language,
    tools: editorPromptState.tools,
    // attributes: {
    //   /* any additional attributes */
    // },
  };
};

/**
 * Map our non-standard editorPromptState to the AgentKernel.
 *
 * Reverse of `getEditorPromptStateFromPrompt`.
 * Explicit list of attributes is a hazard, but required to avoid passing in extra attributes
 * from editorPromptState like linked_tools.
 */
export const editorPromptStateToAgent = ({
  editorPromptState,
  mode,
}: {
  editorPromptState: EditorPromptState;
  mode: EditorMode;
}): AgentKernelRequest => {
  const tools: AgentToolRequest[] | undefined = editorPromptState.tools?.filter(
    isAgentToolRequest,
  ) as AgentToolRequest[];
  return {
    // This nullish coalescing is practically redundant because of how many times this has been standardized, but the types are still optional...
    model: editorPromptState.model ?? DEFAULT_EDITOR_PROMPT_STATE.model,
    endpoint: editorPromptState.endpoint ?? DEFAULT_EDITOR_PROMPT_STATE.endpoint,
    template: (mode === "chat" ? editorPromptState.chat_template : editorPromptState.prompt_template) ?? null,
    provider: editorPromptState.provider ?? DEFAULT_EDITOR_PROMPT_STATE.provider,
    max_tokens: editorPromptState.max_tokens,
    temperature: editorPromptState.temperature,
    top_p: editorPromptState.top_p,
    stop: editorPromptState.stop,
    frequency_penalty: editorPromptState.frequency_penalty,
    presence_penalty: editorPromptState.presence_penalty,
    other: editorPromptState.other,
    seed: editorPromptState.seed,
    reasoning_effort: editorPromptState.reasoning_effort,
    response_format: editorPromptState.response_format,
    template_language: editorPromptState.template_language,
    tools: tools ?? [],
    max_iterations: editorPromptState.max_iterations ?? DEFAULT_EDITOR_PROMPT_STATE.max_iterations,
    attributes: {
      /* any additional attributes */
    },
  };
};

/** Map our non-standard editorPromptState to the V5 Agent/PromptCallRequest. */
export const getCallRequest = ({
  agentId,
  promptId,
  editorPromptState,
  inputs,
  mode,
  user,
  source,
  providerApiKeys,
  stream,
}: {
  agentId?: string;
  promptId?: string;
  editorPromptState: EditorPromptState;
  mode: EditorMode;
  inputs: VariablesWithValues;
  user: string;
  source: string;
  providerApiKeys?: Record<string, string>;
  stream: boolean;
}): AgentCallRequest | PromptCallRequest => {
  const id = agentId ?? promptId;
  if (!id) {
    throw new Error("Either agentId or promptId must be provided");
  }
  const kernel = agentId
    ? editorPromptStateToAgent({ editorPromptState, mode })
    : editorPromptStateToPrompt({ editorPromptState, mode });

  const request: AgentCallRequest | PromptCallRequest = {
    id: id,
    ...(agentId ? { agent: kernel } : { prompt: kernel }),
    user,
    inputs,
    provider_api_keys: providerApiKeys,
    stream,
    source,
  };

  if (mode === "chat") {
    request.messages = getMessagesForRequest(editorPromptState.chat_template || []);
  }

  return request;
};

/** Removes any (likely unintentional) empty message from the end.
 *
 * We add in the user message as a convenience after you run, but it was the case that the
 * run would fail and you'd still get this added message, or you pressed run before filling in the
 * message, which i think 100% of the time is not what you want.
 */
export const removeEmptyMessageFromEnd = (messages: ChatMessageData[]) => {
  if (messages.length === 0) return messages;
  console.log("Removing empty message from end");
  const lastMessage = messages[messages.length - 1];

  if (lastMessage.role === "user" && lastMessage.content === "") {
    console.log("Last message was empty user message. Removing.");
    console.log({ messages });
    return messages.slice(0, -1);
    // FYI we don't use pop as it mutates the array, and although that's fine if used within
    // immer's produce, it's not fine if used outside of it for the state.
    // We would get an error that can't pop index 0 from the array even though not empty.
  }
  if (lastMessage.role === "assistant" && lastMessage.content === "" && !lastMessage.tool_calls) {
    console.log("Last message was empty assistant message. Removing.");
    console.log({ messages });
    return messages.slice(0, -1);
  }
  return messages;
};

/** Clean out extraneous message properties and remove any empty last message */
export const getMessagesForRequest = (messages: ChatMessageData[]): ChatMessage[] => {
  return removeEmptyMessageFromEnd(messages).map(({ content, ...message }) => {
    const base = {
      ...message,
      content: content === "" ? undefined : content,
    };

    switch (base.role) {
      case "assistant":
        return pick(base, ["role", "content", "name", "tool_calls", "thinking"]);
      case "tool":
        // Noting that out tool messages get mapped to function calls.
        // Function messages allow name, but tool messages do not.
        // In practice, it doesn't seem to cause issues including it.
        return pick(base, ["role", "content", "name", "tool_call_id"]);
      case "system":
      case "user":
      default:
        return pick(base, ["role", "content", "name"]);
    }
  });
};

export type TemplateTool = FileWithSchema | OrgToolConfig; // Template tools must have a schema (i.e. can't be a dataset.)

export const useTemplateTools = ({
  allowTools,
  editorPromptState,
}: {
  allowTools?: boolean;
  editorPromptState: EditorPromptState;
}): TemplateTool[] => {
  const { organization } = useActiveOrganization();
  const { tools: orgTools } = useOrgToolConfigs(allowTools ? organization?.id : undefined, true);
  const templateTools = useMemo(() => {
    const templateTools = editorPromptState?.tools?.filter((tool) => tool.usage?.includes("template")) ?? [];
    const filteredOrgTools =
      orgTools?.filter(
        // We don't allow json_schema tools to be used in the playground since they have no runtime
        // We also don't allow Python tools. Python tools should be used via linked files.
        (tool) => tool.config?.preset_name !== "json_schema" && tool.config?.preset_name !== "python",
      ) ?? [];
    return [
      ...(templateTools.filter((tool) => tool.file && "schema" in tool.file).map((tool) => tool.file) as (File & {
        schema: any;
      })[]),
      ...filteredOrgTools,
    ];
  }, [orgTools, editorPromptState?.tools]);

  return templateTools;
};

export const getSignature = (tool: TemplateTool): string | null => {
  if (isOrgToolConfig(tool)) {
    return tool.config.signature;
  }
  const name = tool.schema.name;
  let args: string[] = [];
  if (tool.schema.parameters && tool.schema.parameters.properties) {
    args = Object.keys(tool.schema.parameters.properties);
  }
  const argsString = args.join(", ");
  return `${name}(${argsString})`;
};

/** Get the initial usage when adding a tool.
 *
 * Handles the cases where a tool cannot be used in a template,
 * ensuring we don't enable it for that.
 *
 */
export const getInitialToolUsage = (file: File): ToolUsage[] => {
  let usage: ToolUsage[] = ["template", "tool_calling"];
  if (
    file.type === "tool" &&
    [
      // Tool types that don't support being used in a template.
      "json_schema",
    ].includes(file.tool_type)
  ) {
    // Remove template usage
    usage = usage.filter((u) => u !== "template");
  }
  console.log({ usage, file });
  return usage;
};
